// AdminOrdersPage — полный список заказов платформы.
// ───────────────────────────────────────────────────────
// На главном AdminDashboard в секции «Последние заказы» — только превью 5.
// Сюда переходим за полным списком: status-табы + search по email/title +
// пагинация. Backend всё считает сам, фронт только рендерит.

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ListChecks,
  Search,
  Loader2,
  CheckCircle2,
  Clock3,
  XCircle,
  Inbox,
  ArrowRight,
} from 'lucide-react';
import { useGetAdminOrdersQuery } from '../../api/apiSlice';
import Pagination from '../../components/common/Pagination';

const fmtKzt = (n) => `${Number(n || 0).toLocaleString('ru-RU')} ₸`;
const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString('ru-RU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

// status-tabs: «все» + 3 основные. Бэк отдаёт счётчики по всем статусам
// в statusCounts, считаем сумму для «все».
const STATUS_TABS = [
  { id: '', label: 'Все', icon: ListChecks },
  { id: 'paid', label: 'Оплачены', icon: CheckCircle2 },
  { id: 'pending', label: 'Ожидают', icon: Clock3 },
  { id: 'cancelled', label: 'Отменены', icon: XCircle },
];

const StatusBadge = ({ status }) => {
  const cfg = {
    paid: { label: 'Оплачен', cls: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
    pending: { label: 'Ожидает', cls: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300' },
    cancelled: { label: 'Отменён', cls: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' },
    failed: { label: 'Ошибка', cls: 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300' },
    refunded: { label: 'Возврат', cls: 'bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300' },
  }[status] || { label: status, cls: 'bg-gray-100 text-gray-700' };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

const AdminOrdersPage = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounce поиска. На каждое нажатие не дёргаем сеть.
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Reset на 1-ю страницу при смене фильтров.
  useEffect(() => {
    setPage(1);
  }, [status, search]);

  const { data, isLoading, isFetching } = useGetAdminOrdersQuery({
    page,
    limit: 20,
    status: status || undefined,
    search: search || undefined,
  });

  const items = data?.items || [];
  const counts = data?.statusCounts || {};
  const totalAll = Object.values(counts).reduce((s, n) => s + n, 0);

  // Счётчик таба должен включать «алиас»-статусы:
  //   • Оплачены = paid + confirmed (P2P)
  //   • Ожидают  = pending + pending_contact (P2P)
  // Иначе циферка на табе не совпадает с тем что реально показывается
  // после фильтрации по бэку.
  const countForTab = (id) => {
    if (!id) return totalAll;
    if (id === 'paid') return (counts.paid || 0) + (counts.confirmed || 0);
    if (id === 'pending') return (counts.pending || 0) + (counts.pending_contact || 0);
    return counts[id] || 0;
  };

  // «Показано N-M из K» — диапазон видимых элементов на текущей странице.
  const rangeStart = items.length ? (data.page - 1) * data.pageSize + 1 : 0;
  const rangeEnd = items.length ? rangeStart + items.length - 1 : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <ListChecks className="w-7 h-7 text-indigo-600" />
          {t('adminOrders.title', 'Все заказы')}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('adminOrders.subtitle', 'Полный список покупок на платформе с пагинацией и фильтрами.')}
        </p>
      </div>

      {/* Search */}
      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Поиск по email, имени или названию курса"
          className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
        />
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {STATUS_TABS.map((tab) => {
          const Icon = tab.icon;
          const active = status === tab.id;
          const count = countForTab(tab.id);
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setStatus(tab.id)}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition ${
                active
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${
                  active
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-12 text-center">
          <Inbox className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Заказов не найдено
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Попробуйте изменить фильтры или сбросить поиск.
          </p>
        </div>
      ) : (
        <>
          <div
            className={`rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden transition-opacity ${
              isFetching ? 'opacity-60' : ''
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase text-gray-500 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Пользователь</th>
                    <th className="px-4 py-3 text-left font-semibold">Курс</th>
                    <th className="px-4 py-3 text-right font-semibold">Сумма</th>
                    <th className="px-4 py-3 text-left font-semibold">Статус</th>
                    <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">Дата</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {items.map((o) => (
                    <tr key={o._id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                      <td className="px-4 py-3 max-w-[260px]">
                        <div className="truncate text-gray-900 dark:text-white font-medium">
                          {o.user?.name || '—'}
                        </div>
                        <div className="text-xs text-gray-500 truncate">{o.user?.email || ''}</div>
                      </td>
                      <td className="px-4 py-3 max-w-[320px]">
                        <div className="truncate text-gray-700 dark:text-gray-200">
                          {o.course?.title || '—'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-900 dark:text-white font-semibold">
                        {fmtKzt(o.amount)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={o.status} />
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap text-xs">
                        {fmtDate(o.paidAt || o.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer всегда видим: текст «N-M из K» + (если страниц >1) сам pagination.
              Раньше при <20 заказах footer не отрисовывался, и админ
              не понимал — то ли всё показано, то ли есть скрытое. */}
          {data && (
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                Показано <b className="text-gray-700 dark:text-gray-200">{rangeStart}–{rangeEnd}</b>
                {' из '}
                <b className="text-gray-700 dark:text-gray-200">{data.total}</b>
                {data.pageCount > 1 && (
                  <span className="ml-2 text-gray-400">
                    · стр. {data.page} из {data.pageCount}
                  </span>
                )}
              </div>
              {data.pageCount > 1 && (
                <Pagination
                  page={data.page}
                  pageCount={data.pageCount}
                  onChange={(p) => {
                    setPage(p);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminOrdersPage;
